import React, { useCallback, useMemo, useEffect, useState } from "react";
import { useBreakpointsContext, useScroll } from "../../hooks";
import {
    ITINERARY_HEADER_HEIGHT,
    ITINERARY_HEADER_HEIGHT_MOBILE,
    DAY_GROUP_HEADER_HEIGHT,
    DAY_GROUP_HEADER_HEIGHT_MOBILE,
    MAP_HEIGHT_MOBILE,
    MAP_HEADER_HEIGHT_MOBILE,
} from "../../constants";
import { connect } from "../../store";
import ItineraryStops from "./ItineraryStops";
import find from "lodash.find";
import { getNearestStopId } from "../layout/utils";

function getDistances(stops) {
    // distance traveled per day
    //&TODO Get distance traveled per hour from API
    let day = 1;

    const result = stops
        .filter(stop => "stopType" in stop)
        .reduce(function(acc, stop, idx, list) {
            //If day 1 is an overnight stop, push distance travelled to the next day
            //Distance between the overnight stop and the next stop is, of course, part of the next day

            //Cover first days with one Overnight stop only
            if (idx === 0 && stop.overnight) {
                day += 1;
                return {
                    ...acc,
                    [day - 1]: "",
                    [day]: stop.distToNext,
                };
            } else if (idx === 0) {
                return {
                    ...acc,
                    [day]: stop.distToNext,
                };
            }

            if (stop.overnight) {
                day += 1;
                return {
                    ...acc,
                    //[day - 1]: list[idx - 1].distToNext,
                    [day]: (acc[day] || 0) + stop.distToNext,
                };
            }

            return { ...acc, [day]: (acc[day] || 0) + stop.distToNext };
        }, {});

    return result;
}

const useProgressByDay = (inPlanner, cardsOffsetTop) => {
    const [progressByDay, setProgressByDay] = useState({});
    const { isDesktop } = useBreakpointsContext();
    //Calculate scroll progress for each Day group
    useScroll(
        useCallback(() => {
            const scrollY = window.scrollY;
            const progressByDay = {};
            document.querySelectorAll(".trpr__card-group").forEach((dayGroupEl, index) => {
                const { height: dayGroupHeight } = dayGroupEl.getBoundingClientRect();
                const mapHeight = !isDesktop ? MAP_HEIGHT_MOBILE + (inPlanner ? 0 : MAP_HEADER_HEIGHT_MOBILE) : 0;
                const headerHeight = !isDesktop ? ITINERARY_HEADER_HEIGHT_MOBILE : ITINERARY_HEADER_HEIGHT;
                const dayGroupHeader = !isDesktop ? DAY_GROUP_HEADER_HEIGHT_MOBILE : DAY_GROUP_HEADER_HEIGHT;
                const maxScrollPosition = dayGroupHeight - dayGroupHeader - mapHeight;
                const currentScrollPosition = scrollY + headerHeight - dayGroupEl.offsetTop;
                let value;
                if (currentScrollPosition > maxScrollPosition) {
                    value = 100;
                } else if (currentScrollPosition < 0) {
                    value = 0;
                } else {
                    value = (100 * currentScrollPosition) / maxScrollPosition;
                }
                progressByDay[index + 1] = value;

                //Check if day has reached overnight stop
                const overnightStopCardEl = dayGroupEl.querySelector(".js__stop-card-overnight");
                if (!overnightStopCardEl) {
                    return;
                }
                const { y: overnightCardY } = overnightStopCardEl.getBoundingClientRect();
                const hasReachedByStopOvernight = overnightCardY <= cardsOffsetTop;
                if (hasReachedByStopOvernight) {
                    progressByDay[index + 1] = 100;
                }
            });
            setProgressByDay(progressByDay);
        }, [isDesktop, inPlanner, cardsOffsetTop]),
        100,
    );
    return progressByDay;
};

const useItemsGroupByDay = ({ stops, formattedStops, loopDirection, startingPoint, routeType }) => {
    return useMemo(() => {
        if (!formattedStops.length) {
            return [];
        }
        const customStopsById = formattedStops.reduce((result, stop) => ({ ...result, [stop.id]: stop }), {});
        let customOrder = -1;
        let savedDistance;
        let savedTween;
        let savedPlaceStart;
        const addToGroup = (item, result, isPrevItemOvernight) => {
            if (isPrevItemOvernight && ["day"].includes(item.type)) {
                // create new group
                return [...result, [item]];
            }
            // add to last group
            return [...result.slice(0, result.length - 1), [...(result?.[result.length - 1] ?? []), item]];
        };
        const addSavedDistanceTo = (result, isPrevItemOvernight) => {
            let _result = [...result];
            if (savedDistance) {
                _result = addToGroup(savedDistance, result, isPrevItemOvernight);
                savedDistance = null;
            }
            return _result;
        };
        const addSavedTweenTo = (result, isPrevItemOvernight) => {
            let _result = [...result];
            if (savedTween) {
                _result = addToGroup(savedTween, result, isPrevItemOvernight);
                savedTween = null;
            }
            return _result;
        };
        const addSavedPlaceStartTo = (result, isPrevItemOvernight) => {
            let _result = [...result];
            if (savedPlaceStart) {
                _result = addToGroup(savedPlaceStart, result, isPrevItemOvernight);
                savedPlaceStart = null;
            }
            return _result;
        };
        let items = loopDirection === "COUNTER_CLOCKWISE" ? [stops[0], ...stops.slice(1).reverse()] : stops;
        const hasPlace = stops.find(stop => stop.type === "place_start");
        if (loopDirection === "COUNTER_CLOCKWISE") {
            items = items.map((item, index, _items) => {
                if (!["place_start", "place_end"].includes(item.type)) {
                    return item;
                }
                if ("place_end" === item.type) {
                    //Switch place_end with next place_start
                    return _items.slice(index + 1).find(item => item.type === "place_start");
                }
                //Switch place_start with previous place_end
                return _items
                    .slice(0, index)
                    .reverse()
                    .find(item => item.type === "place_end");
            });
        }
        if (startingPoint?.coordinates?.length && routeType?.toUpperCase() === "LOOP" && !hasPlace) {
            const startStop = find(formattedStops, {
                id: getNearestStopId(
                    formattedStops.filter(stop => stop.type === "stop"),
                    startingPoint.coordinates,
                ),
            });
            const startIndex = items.findIndex(item => item.id === startStop.id);
            items = [...items.slice(startIndex), ...items.slice(0, startIndex)].map(function(item, idx) {
                return { ...item, order: idx };
            });
        }
        const lastStopId = Array.from(items)
            .reverse()
            .find(item => item.type === "stop").id;
        let lastDayNumberFixed = 0;
        return items.reduce((result, item, index) => {
            let _result = [...result];
            const prevStop = items
                .slice(0, index)
                .reverse()
                .find(stop => ["stop", "place_overnight"].includes(stop.type));
            const isPrevItemOvernight =
                prevStop?.stopType?.label?.toUpperCase?.() === "OVERNIGHT" || prevStop?.type === "place_overnight";
            const isLastItem = index === items.length - 1;
            if (index === 0 || (item.type === "stop" && isPrevItemOvernight)) {
                const dayNumber = _result.length + 1;
                const dayNumberFixed = lastDayNumberFixed + parseInt(prevStop?.multiNight?.numberOfNights ?? 1);
                lastDayNumberFixed = dayNumberFixed;
                const day = {
                    id: item.id,
                    dayNumber, //Used for identify the day
                    dayNumberFixed, //Day number shown to the user
                    type: "day",
                };
                _result = addToGroup(day, _result, isPrevItemOvernight);
                _result = addSavedDistanceTo(_result, isPrevItemOvernight);
                _result = addSavedTweenTo(_result, isPrevItemOvernight);
                _result = addSavedPlaceStartTo(_result, isPrevItemOvernight);
            }
            if (item.type === "place_start") {
                const stopsUntilPlaceEnd = items
                    .slice(index + 1, index + 1 + items.slice(index + 1).findIndex(stop => stop.type === "place_end"))
                    .filter(stop => stop.type === "stop");
                const stopsUntilPlaceEndCount = stopsUntilPlaceEnd.length;
                const placeStart = {
                    ...item,
                    stopsUntilPlaceEnd,
                    stopsUntilPlaceEndCount,
                };
                if (isPrevItemOvernight) {
                    savedPlaceStart = placeStart;
                } else {
                    _result = addSavedDistanceTo(_result, isPrevItemOvernight);
                    _result = addToGroup(placeStart, _result, isPrevItemOvernight);
                }
            }
            if (item.type === "place_end") {
                _result = addToGroup(item, _result, isPrevItemOvernight);
            }
            if (item.type === "tween") {
                if (isPrevItemOvernight) {
                    savedTween = item;
                } else {
                    _result = addSavedDistanceTo(_result, isPrevItemOvernight);
                    _result = addToGroup(item, _result, isPrevItemOvernight);
                }
            }
            if (item.type === "place_overnight") {
                const distance = {
                    ...customStopsById[item.id],
                    type: "distance",
                };
                _result = addToGroup(distance, _result, false);
                _result = addToGroup(item, _result, isPrevItemOvernight);
            }
            if (item.type === "stop") {
                customOrder += 1;
                _result = addSavedDistanceTo(_result, isPrevItemOvernight);
                const stop = {
                    ...customStopsById[item.id],
                    customOrder,
                    stopNumber: customOrder + 1,
                    isLastStop: item.id === lastStopId,
                };
                _result = addToGroup(stop, _result, isPrevItemOvernight);
                if (!isLastItem) {
                    savedDistance = {
                        ...customStopsById[item.id],
                        type: "distance",
                    };
                }
            }
            if (isLastItem) {
                _result = addSavedTweenTo(_result, isPrevItemOvernight);
                // TODO: add end of itinerary item tween
            }
            return _result;
        }, []);
    }, [stops, formattedStops, loopDirection, startingPoint, routeType]);
};

function ItineraryStopsContainer(props) {
    const {
        dispatch,
        formattedStops,
        itineraryId,
        stopsRaw,
        loopDirection,
        activeStopId,
        className,
        itineraryMetadata,
        dayTopOffset,
        activeDayId,
        startingPoint,
        routeType,
        setIsOpenItinerarySwitcher,
        showItinerarySwitcher,
        inLauncherModal,
        inPlanner,
        useTwoColumnLayout,
        useOldLayout,
        cardsOffsetTop,
        activeSuggestionId,
        setIsEmailModalVisible,
        showToast,
    } = props;
    const distances = useMemo(() => getDistances(formattedStops), [formattedStops]);
    const progressByDay = useProgressByDay(inPlanner, cardsOffsetTop);
    const stopsRawById = useMemo(() => {
        return stopsRaw.reduce((result, stop) => ({ ...result, [stop.id]: stop }), {});
    }, [stopsRaw]);
    const itemsGroupByDay = useItemsGroupByDay({
        formattedStops,
        stops: stopsRaw,
        loopDirection,
        stopsById: stopsRawById,
        startingPoint,
        routeType,
    });
    const daysById = useMemo(() => {
        return itemsGroupByDay.flat().reduce((result, item) => {
            if (item.type === "day") {
                return {
                    ...result,
                    [`${item.id}-day`]: item,
                };
            }
            return result;
        }, {});
    }, [itemsGroupByDay]);

    useEffect(() => {
        dispatch({
            type: "SET_PROGRESS_BY_DAY",
            payload: progressByDay,
        });
    }, [dispatch, progressByDay]);

    return (
        <ItineraryStops
            className={className}
            items={stopsRaw}
            itemsGroupByDay={itemsGroupByDay}
            stopsById={stopsRawById}
            itineraryId={itineraryId}
            distancesByStopDayNumber={distances}
            activeStopId={activeStopId}
            itineraryMetadata={itineraryMetadata}
            dayTopOffset={dayTopOffset}
            activeDayId={activeDayId}
            setIsOpenItinerarySwitcher={setIsOpenItinerarySwitcher}
            showItinerarySwitcher={showItinerarySwitcher}
            inLauncherModal={inLauncherModal}
            useTwoColumnLayout={useTwoColumnLayout}
            useOldLayout={useOldLayout}
            daysById={daysById}
            activeSuggestionId={activeSuggestionId}
            setIsEmailModalVisible={setIsEmailModalVisible}
            showToast={showToast}
        />
    );
}

const mapStateToProps = () => ({});

export default connect(mapStateToProps)(ItineraryStopsContainer);
